import { createContext, useContext, useState, useEffect } from 'react';
import apiClient from '../api/client';

interface AppInfo {
  siteName: string;
  logoUrl: string | null;
  onlineName: string;
}

const AppInfoContext = createContext<AppInfo>({ siteName: 'FitTrack', logoUrl: null, onlineName: '' });

/**
 * Parses key color values from the gym's OnlinePackagePackagesCss and injects
 * them as CSS custom properties on :root, overriding the PWA's default theme.
 *
 * Rules extracted:
 *   body { background-color } → --bg
 *   body { color }            → --text-primary
 *   .fittrackonlinebutton { color } → --accent, --nav-active
 */
function applyTenantTheme(css: string) {
  const overrides: Record<string, string> = {};

  const bodyBlock = /body\s*\{([^}]*)\}/i.exec(css);
  if (bodyBlock) {
    const bgMatch = /background-color\s*:\s*([^;}\n]+)/i.exec(bodyBlock[1]);
    if (bgMatch) overrides['--bg'] = bgMatch[1].trim();

    // match "color:" but not "background-color:"
    const colorMatch = /(?<![-a-z])color\s*:\s*([^;}\n]+)/i.exec(bodyBlock[1]);
    if (colorMatch) overrides['--text-primary'] = colorMatch[1].trim();
  }

  const btnBlock = /\.fittrackonlinebutton\s*\{([^}]*)\}/i.exec(css);
  if (btnBlock) {
    const colorMatch = /(?<![-a-z])color\s*:\s*([^;}\n]+)/i.exec(btnBlock[1]);
    if (colorMatch) {
      const accent = colorMatch[1].trim();
      overrides['--accent'] = accent;
      overrides['--nav-active'] = accent;
    }
  }

  if (Object.keys(overrides).length === 0) return;

  let style = document.getElementById('tenant-theme') as HTMLStyleElement | null;
  if (!style) {
    style = document.createElement('style');
    style.id = 'tenant-theme';
    document.head.appendChild(style);
  }
  style.textContent = `:root {\n${Object.entries(overrides)
    .map(([k, v]) => `  ${k}: ${v};`)
    .join('\n')}\n}`;
}

export function AppInfoProvider({ children }: { children: React.ReactNode }) {
  const [info, setInfo] = useState<AppInfo>({ siteName: 'FitTrack', logoUrl: null, onlineName: '' });

  useEffect(() => {
    apiClient
      .get('/Authentication/AppInfo')
      .then((res) => {
        const d = res.data?.data;
        if (d) {
          setInfo({ siteName: d.siteName ?? 'FitTrack', logoUrl: d.logoUrl ?? null, onlineName: d.onlineName ?? '' });
          if (d.themeCss) applyTenantTheme(d.themeCss);
        }
      })
      .catch(() => {/* app still works without branding */});
  }, []);

  return <AppInfoContext.Provider value={info}>{children}</AppInfoContext.Provider>;
}

export function useAppInfo() {
  return useContext(AppInfoContext);
}
